import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const EMPTY = { name: "", slug: "", description: "", icon: "Compass", order: 0, active: true };

export default function Categories() {
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/categories").then(({ data }) => setCats(data));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    const payload = { ...form, slug: form.slug || slugify(form.name), order: Number(form.order) || 0 };
    try {
      if (editing) await api.put(`/admin/categories/${editing.id}`, payload); else await api.post("/admin/categories", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (c) => { await api.delete(`/admin/categories/${c.id}`); toast.success("Deleted"); load(); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-4xl font-semibold">Categories</h1><p className="text-muted-foreground">Manage the travel categories shown on the site.</p></div>
        <Button className="rounded-full" onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} data-testid="add-category"><Plus className="h-4 w-4 mr-1" /> Add Category</Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {cats.map((c) => (
          <div key={c.id} className="rounded-xl border bg-card p-5" data-testid={`cat-${c.slug}`}>
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-2xl">{c.name}</h3>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => { setEditing(c); setForm({ ...EMPTY, ...c }); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                <AlertDialog><AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-destructive"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                  <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete {c.name}?</AlertDialogTitle></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(c)} className="bg-destructive">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{c.description}</p>
            <p className="text-xs font-mono text-muted-foreground mt-2">/{c.slug} · {c.active ? "Active" : "Hidden"}</p>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle className="font-heading text-2xl">{editing ? "Edit" : "Add"} Category</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="cat-name" /></div>
            <div><Label>Slug</Label><Input value={form.slug} onChange={(e) => set("slug", slugify(e.target.value))} placeholder={slugify(form.name)} /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Icon (lucide name)</Label><Input value={form.icon} onChange={(e) => set("icon", e.target.value)} placeholder="Mountain" /></div>
              <div><Label>Order</Label><Input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} /></div>
            </div>
            <label className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => set("active", v)} /> Active</label>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-category">Save</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
